import ValueFormatter from '../../model/ValueFormatter';
import type {AxisItem} from '../cube/CubeIndex';
import {CubeCellValue, CubeMeasure, RenderContext} from '../types';

export function decimalSeparator(locale: string): string {
    return (1.1).toLocaleString(locale).substring(1, 2);
}

function formatter(measure: CubeMeasure, ctx: RenderContext, withUnit: boolean): ValueFormatter {
    return new ValueFormatter(measure.precision, withUnit ? measure.unit ?? '' : '', '-', decimalSeparator(ctx.locale));
}

export function formatValue(value: number | null | undefined, measure: CubeMeasure, ctx: RenderContext): string {
    return formatter(measure, ctx, true).format(value);
}

export function roundValue(value: number | null | undefined, measure: CubeMeasure): number | null {
    return new ValueFormatter(measure.precision).getRoundedValue(value);
}

export interface Delta {
    value: number;
    text: string;
    sign: -1 | 0 | 1;
}

/** Signed change; without a measure it is a whole number (a rank change). */
export function formatDelta(value: number, measure: CubeMeasure | undefined, ctx: RenderContext): string {
    const text = measure ? formatValue(value, measure, ctx) : value.toLocaleString(ctx.locale, {maximumFractionDigits: 0});

    return (value > 0 ? '+' : '') + text;
}

/** Difference of rounded values (what the user sees), null when there is nothing to compare. */
export function computeDelta(
    current: number | null | undefined,
    previous: number | null | undefined,
    measure: CubeMeasure,
    ctx: RenderContext,
): Delta | null {
    const now = roundValue(current, measure);
    const before = roundValue(previous, measure);
    if (now === null || before === null) {
        return null;
    }
    const value = roundValue(now - before, measure) ?? 0;
    const sign = value > 0 ? 1 : value < 0 ? -1 : 0;

    return {value, sign, text: formatDelta(value, measure, ctx)};
}

/** Rounded previous value; a previous term without respondents is no base to compare with */
export function previousValue(cell: CubeCellValue | undefined, measure: CubeMeasure): number | null {
    return cell && (cell.previousCount ?? 0) > 0 ? roundValue(cell.previous, measure) : null;
}

export function escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Respondents of a cell: count is a sum of weights on a weighted project */
export function respondents(cell: CubeCellValue | undefined): number {
    return cell?.n ?? cell?.count ?? 0;
}

export function previousRespondents(cell: CubeCellValue | undefined): number {
    return cell?.previousN ?? cell?.previousCount ?? 0;
}

export function formatCount(cell: CubeCellValue | undefined, lowBase: boolean): string {
    return `n=${respondents(cell)}${lowBase ? '*' : ''}`;
}

/** Count text of a tooltip; low base is red */
export function countHtml(cell: CubeCellValue | undefined, lowBase: boolean): string {
    return `<span${lowBase ? ' style="color:red"' : ''}>${formatCount(cell, lowBase)}</span>`;
}

export function categoryLabel(item: AxisItem): string {
    const label = escapeHtml(item.label);
    return item.member?.role === 'total' ? `<b>${label}</b>` : label;
}

/** Value format of an axis label with the measure unit (escaped once, it ends up in a Highcharts format string). */
export function axisLabelFormat(measure: CubeMeasure | undefined): string {
    return measure?.unit ? `{value}${escapeHtml(measure.unit)}` : '{value}';
}

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/**
 * ISO 8601 wall-clock timestamp (no offset) as a localized date, with the time unless it is exactly midnight (date-only
 * imports carry no time). The parts are read as they are and formatted in
 * UTC, so the viewer's browser timezone cannot shift them. Anything else is shown as it came.
 */
export function formatDateTime(iso: string, locale: string): string {
    const parts = WALL_CLOCK.exec(iso);
    if (!parts) {
        return iso;
    }
    const [year, month, day, hour, minute] = parts.slice(1).map(Number);

    return new Intl.DateTimeFormat(locale, {dateStyle: 'medium', ...(hour === 0 && minute === 0 ? {} : {timeStyle: 'short'}), timeZone: 'UTC'})
        .format(new Date(Date.UTC(year, month - 1, day, hour, minute)));
}

/** `YYYY-MM-DD HH:mm` of a wall-clock timestamp: locale-free, sorts as text in a spreadsheet */
export function dateTimeCsv(iso: string): string {
    const parts = WALL_CLOCK.exec(iso);

    return parts ? `${parts[1]}-${parts[2]}-${parts[3]} ${parts[4]}:${parts[5]}` : iso;
}

export function noDataHtml(text: string): string {
    return `<div class="text-center text-muted p-4">${escapeHtml(text)}</div>`;
}

export interface TooltipInput {
    heading: string[];
    color?: string;
    measure: CubeMeasure;
    cell: CubeCellValue | undefined;
    lowBase: boolean;
}

export function tooltipHtml(input: TooltipInput, ctx: RenderContext): string {
    const {heading, color, measure, cell, lowBase} = input;
    const dot = color ? `<span style="color:${color}">●</span> ` : '';
    const head = heading.filter(Boolean).map(escapeHtml).join('<br>');

    return `${dot}${head}<br><b>${formatValue(cell?.value, measure, ctx)}</b>, ${countHtml(cell, lowBase)}${previousHtml(cell, measure, ctx)}`;
}

/** Previous-term value with its delta and count, empty without a comparison */
export function previousText(cell: CubeCellValue | undefined, measure: CubeMeasure, ctx: RenderContext): string {
    if (!cell || cell.previous === undefined || cell.previous === null) {
        return '';
    }
    const delta = computeDelta(cell.value, cell.previous, measure, ctx);
    const deltaText = delta ? ` (${delta.text})` : '';

    return `${formatValue(cell.previous, measure, ctx)}${deltaText}, n=${previousRespondents(cell)}`;
}

/** Previous-term line of a tooltip (with its leading break), empty without a comparison */
export function previousHtml(cell: CubeCellValue | undefined, measure: CubeMeasure, ctx: RenderContext): string {
    const text = previousText(cell, measure, ctx);

    return text ? `<br><span style="color:#909090;font-size:10px">${text}</span>` : '';
}
